import requests, { ErrorTypes } from "@/api/request";
import VuexAsync from "@/plugins/vuexAsync";
import router from "@/router";
import { RootState } from "@/store/types";
import { sendError } from "@/utilities/globalErrorHandler";
import { CLOSE, OOPS } from "@/views/SharedViewStructures";
import { ActionTree, Commit } from "vuex";
import { Permission } from "../user";
import { User } from "../user/types";
import { Patient, PatientState } from "./types";

function handleError(
  commit: any,
  rootGetters: any,
  error: any,
  functionName: string
) {
  const prefix = `Patient action error: function [${functionName}]:`;

  if (error.response && error.response.status === ErrorTypes.UNAUTHORIZED) {
    const isPatient = rootGetters["user/user"].rights.includes(
      Permission.PATIENT
    );
    if (isPatient) {
      // Ask patient to close and reload
      router.push(`/error/${CLOSE}`);
    } else {
      // Ask anyone else to re-login
      router.push("/login");
    }
  } else if (error.response && error.response.status === ErrorTypes.FORBIDDEN) {
    router.push(`/error/${OOPS}`);
  } else {
    // eslint-disable-next-line no-console
    console.error(prefix, error);

    // Only send client side errors, not request specific ones
    if (!error.request) {
      sendError(`${prefix} ${error}`);
    }
    commit("error", { error, errorType: ErrorTypes.UNKNOWN });
  }
}

function toggleMode(mode: string, commit: Commit) {
  switch (mode.toLowerCase()) {
    case "tower":
      commit("toggleTower");
      break;
    case "verify":
      commit("toggleVerify");
      break;
  }
}

const fetchingBody: Record<string, any> = {};

const fetchingMeds: Record<string, any> = {};

const CENSUS_PROMISE_NAMESPACE = "CENSUS_PROMISE_NAMESPACE";
export const actions: ActionTree<PatientState, RootState> = {
  async getAllVars({ commit, rootGetters }): Promise<void> {
    const areas = rootGetters["user/areas"] as string[];
    await Promise.all(
      areas.map(async area => {
        try {
          const vars = await requests.getVars(area);
          commit("setVars", { vars, area });
        } catch (error) {
          handleError(commit, rootGetters, error, "getVars");
        }
      })
    );
    commit("varsLoaded", true);
  },
  fetchCensus(
    { commit, getters, rootGetters },
    { view, unit, filtersAndSorting }
  ): void {
    const caseStates = getters.caseState;

    VuexAsync.makeRequest(
      CENSUS_PROMISE_NAMESPACE,
      requests.getCensus(
        getters.area,
        view,
        unit,
        getters.towerMode,
        filtersAndSorting,
        caseStates
      ),
      ({
        patients,
        stats,
        isTesting
      }: {
        patients: Patient[];
        stats: any;
        isTesting: boolean;
      }) => {
        // Set time first
        commit("stats", stats);
        commit("isTesting", isTesting);
        commit("setAudioAlerts", patients);
        commit("censusUpdate", patients);
        commit("censusLoaded", true);
      },
      (error: any) => {
        handleError(commit, rootGetters, error, "fetchCensus");
        commit("censusLoaded", false);
      }
    );
  },
  clearCensus({ commit }) {
    commit("censusLoaded", false);
    commit("censusUpdate", []);
  },
  clearCareCoordinationNote({ commit }) {
    commit(
      "updateCareCoordinationNote",
      "<p>Care Coordination Note not available.</p>"
    );
  },
  async getCareCoordinationNote({ commit, rootGetters }, id): Promise<any> {
    try {
      const note: string = await requests.getCareCoordinationNote(id);
      commit("updateCareCoordinationNote", note);
    } catch (error) {
      handleError(commit, rootGetters, error, "getCareCoordinationNote");
    }
  },
  async fetchBodyData({ commit, getters, rootGetters }, id): Promise<any> {
    if (fetchingBody[id]) {
      return Promise.resolve();
    }
    try {
      fetchingBody[id] = true;
      const patient: Patient = await requests.getBody(id, getters.area);

      // Set time first
      commit("now", {
        now: patient.body.now,
        dateTimeOffset: patient.body.dateTimeOffset
      });

      commit("setAudioAlerts", [patient]);
      commit("bodyUpdate", patient);

      const isTesting =
        patient.body.testID != null && patient.body.testID !== "";
      commit("isTesting", isTesting);
    } catch (error) {
      handleError(commit, rootGetters, error, "fetchBodyData");
    } finally {
      fetchingBody[id] = false;
    }
  },
  async updateSettings(
    { commit, getters, rootGetters },
    { id, settings }
  ): Promise<any> {
    try {
      // stop updates from going out while we make changes
      fetchingBody[id] = true;
      const user = rootGetters["user/user"] as User;
      const response = await requests.updateSettings(
        getters.area,
        id,
        user.userID,
        settings
      );
      commit("bodyUpdate", response);
    } catch (error) {
      handleError(commit, rootGetters, error, "updateSettings");
    } finally {
      fetchingBody[id] = false;
    }
  },
  toggleMode({ commit }, modes: string) {
    const modesToUpdate = modes.split(",");
    modesToUpdate.forEach(mode => {
      toggleMode(mode, commit);
    });
  },
  singlePatientView({ commit }) {
    commit("singlePatientView");
  },
  markRead({ commit }, audioAlertKey: string) {
    commit("markRead", audioAlertKey);
  },
  setIsTesting({ commit }, isTesting) {
    commit("isTesting", isTesting);
  },
  setIsPagingTest({ commit }, isPagingTest) {
    commit("isPagingTest", isPagingTest);
  },
  setIsPagingTestActive({ commit }, isPagingTestActive) {
    commit("isPagingTestActive", isPagingTestActive);
  },
  setIsActiveTest({ commit }, isActiveTest) {
    commit("isActiveTest", isActiveTest);
  },

  // Meds
  async getMeds({ commit, getters, rootGetters }, id): Promise<any> {
    if (fetchingMeds[id]) {
      return Promise.resolve();
    }
    try {
      fetchingBody[id] = true;
      const meds: any = await requests.getMedData(getters.area, id);
      commit("updateMedList", meds);
    } catch (error) {
      handleError(commit, rootGetters, error, "getMeds");
    } finally {
      fetchingBody[id] = false;
    }
  }
};
